import type { ListFilter } from '@/lib/types';
import { isBoolType, isDateType, isNumericType, parseDateInput, parseNumberInput } from '@/lib/format';

/**
 * Filtre satırına yazılan ifadeyi sunucu filtresine çevirir.
 *  Metin : abc (içerir) · =abc (eşit) · ^abc (ile başlar) · !abc (içermez) · boş/dolu: (boş) / (dolu)
 *  Sayı  : 10 · >10 · >=10 · <5 · <=5 · !=3 · 10..20
 *  Tarih : 01.02.2024 (o gün) · >01.02.2024 · 01.01.2024..31.01.2024
 *  Mantık: evet / hayır
 */
export function parseFilter(field: string, raw: string, sqlType?: string): ListFilter[] {
  const s = raw.trim();
  if (!s) return [];
  if (s === '(boş)') return [{ field, op: 'empty' }];
  if (s === '(dolu)') return [{ field, op: 'notEmpty' }];
  if (isBoolType(sqlType)) {
    const v = /^(e|evet|1|true|✓)$/i.test(s) ? true : /^(h|hayır|hayir|0|false)$/i.test(s) ? false : null;
    return v === null ? [] : [{ field, op: 'eq', value: v ? 1 : 0 }];
  }
  if (isNumericType(sqlType)) {
    const range = /^(.+?)\.\.(.+)$/.exec(s);
    if (range) {
      const a = parseNumberInput(range[1]), b = parseNumberInput(range[2]);
      return a != null && b != null ? [{ field, op: 'between', value: a, value2: b }] : [];
    }
    const m = /^(>=|<=|!=|<>|>|<|=)?\s*(.+)$/.exec(s)!;
    const n = parseNumberInput(m[2]);
    if (n == null) return [];
    const ops: Record<string, 'gte' | 'lte' | 'neq' | 'gt' | 'lt' | 'eq'> = { '>=': 'gte', '<=': 'lte', '!=': 'neq', '<>': 'neq', '>': 'gt', '<': 'lt', '=': 'eq' };
    const op = ops[m[1] ?? '='] ?? 'eq';
    return [{ field, op, value: n }];
  }
  if (isDateType(sqlType)) {
    const range = /^(.+?)\.\.(.+)$/.exec(s);
    if (range) {
      const a = parseDateInput(range[1]), b = parseDateInput(range[2]);
      return a && b ? [{ field, op: 'between', value: a, value2: endOfDay(b) }] : [];
    }
    const m = /^(>=|<=|>|<|=)?\s*(.+)$/.exec(s)!;
    const d = parseDateInput(m[2]);
    if (!d) return [];
    if (!m[1] || m[1] === '=') return [{ field, op: 'between', value: d, value2: endOfDay(d) }];
    const ops: Record<string, 'gte' | 'lte' | 'gt' | 'lt'> = { '>=': 'gte', '<=': 'lte', '>': 'gt', '<': 'lt' };
    const op = ops[m[1]];
    return [{ field, op, value: op === 'gt' || op === 'lte' ? endOfDay(d) : d }];
  }
  if (s.startsWith('=')) return [{ field, op: 'eq', value: s.slice(1) }];
  if (s.startsWith('^')) return [{ field, op: 'startsWith', value: s.slice(1) }];
  if (s.startsWith('!')) return [{ field, op: 'notContains', value: s.slice(1) }];
  return [{ field, op: 'contains', value: s }];
}

function endOfDay(iso: string) {
  return iso.slice(0, 10) + 'T23:59:59';
}
